import {
  Button,
  InfoBox,
  InputField,
  InputLabel,
  InputWrapper,
  MtFormButtons,
  MtFormContainer,
  MtImagesArray,
  MtLayouTitleEditButton,
  MtLayouTitleEditButtonContainer,
  MtLayoutTitleWrapper,
  SectionTitle,
} from '@presentation/components';
import { MtLayouTitleBackButton } from '@presentation/components/MtLayout/styled';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { FaChevronLeft, FaEdit, FaExternalLinkAlt, FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import type { MtLitterPageFormValues, MtLitterPageProps } from '..';
import bornLitter from './assets/born_litter.svg';
import futureLitter from './assets/future_litter.svg';
import { FormStepDogs } from './components/FormStepDogs';
import { FormStepGeneral } from './components/FormStepGeneral';
import { PageSkeleton } from './components/PageSkeleton';
import * as Styled from './styled';

export * from './types';

export const MtLitterPage: FC<MtLitterPageProps> = (props) => {
  const {
    mode,
    onEditClick,
    onCancelClick,
    onDeleteClick,
    onBackClick,
    adminValidated,
    archived = false,
    loading = false,
    showImageError = false,
    onSubmitClick,
    link,
    log,
  } = props;
  const { translate } = useTranslation();
  const { resetForm, setFieldValue, values, errors, isSubmitting } =
    useFormikContext<MtLitterPageFormValues>();

  return (
    <MtFormContainer>
      {loading && <PageSkeleton />}

      {!loading && (
        <>
          {!!onBackClick && (
            <MtLayouTitleBackButton onClick={onBackClick}>
              <FaChevronLeft />
              <div>{translate('common.buttons.back')}</div>
            </MtLayouTitleBackButton>
          )}
          <MtLayoutTitleWrapper>
            <SectionTitle>
              {mode === 'new' && translate('mt.breeder.litters.new.title')}
            </SectionTitle>
            {!archived && (
              <MtLayouTitleEditButtonContainer>
                {mode === 'view' && (
                  <MtLayouTitleEditButton onClick={onEditClick} color="blue">
                    <span>{translate('common.buttons.edit')}</span>
                    <FaEdit />
                  </MtLayouTitleEditButton>
                )}
                {mode !== 'new' && (
                  <MtLayouTitleEditButton onClick={onDeleteClick} color="red">
                    <span>{translate('common.buttons.archive')}</span>
                    <FaTrash />
                  </MtLayouTitleEditButton>
                )}
                {mode !== 'new' && link && (
                  <Link target="_blank" to={link}>
                    <MtLayouTitleEditButton color="dark">
                      <span>{translate('common.buttons.seeOnTipaw')}</span>
                      <FaExternalLinkAlt />
                    </MtLayouTitleEditButton>
                  </Link>
                )}
              </MtLayouTitleEditButtonContainer>
            )}
          </MtLayoutTitleWrapper>

          <>
            {!archived && adminValidated === false && (
              <InfoBox warning="true" marginBottom={16}>
                {translate('mt.breeder.litters.waitingValidation')}
              </InfoBox>
            )}

            {adminValidated === undefined && values.born !== undefined && (
              <InfoBox marginBottom={16}>{translate('mt.breeder.litters.archivingInfo')}</InfoBox>
            )}

            {values.born == null ? (
              <Styled.BornContainer>
                <Styled.BornWrapper blueVariant="light" onClick={() => setFieldValue('born', true)}>
                  <Styled.BornImage src={bornLitter} alt="bornLitter" />
                  <Styled.BornTitle
                    dangerouslySetInnerHTML={{
                      __html: translate('mt.breeder.litters.new.titleNow'),
                    }}
                  />
                  <Button color="blue">{translate('common.buttons.addLitterAd')}</Button>
                </Styled.BornWrapper>
                <Styled.BornWrapper
                  blueVariant="lightBlue"
                  onClick={() => setFieldValue('born', false)}
                >
                  <Styled.BornImage src={futureLitter} alt="bornLitter" />
                  <Styled.BornTitle
                    dangerouslySetInnerHTML={{
                      __html: translate('mt.breeder.litters.new.titleFuture'),
                    }}
                  />
                  <Button color="blue">{translate('common.buttons.addLitterAd')}</Button>
                </Styled.BornWrapper>
              </Styled.BornContainer>
            ) : (
              <>
                <InputWrapper>
                  <InputWrapper split>
                    <InputLabel required>{translate('common.inputs.dogPictures.label')}</InputLabel>
                    {errors.images && showImageError && (
                      <Styled.ErrorText>{errors.images}</Styled.ErrorText>
                    )}
                    <MtImagesArray mode={mode} values={values} log={log} />

                    <InputWrapper>
                      <InputLabel required>
                        {translate('common.inputs.description.label')}
                      </InputLabel>
                      <InfoBox>{translate('mt.breeder.useChatInfo')}</InfoBox>
                      <InputField disabled={mode === 'view'} name="description" textarea />
                    </InputWrapper>
                  </InputWrapper>

                  <InputWrapper split last>
                    <FormStepGeneral {...props} />
                    {values.born && <FormStepDogs {...props} />}
                  </InputWrapper>
                </InputWrapper>

                <MtFormButtons
                  showCancel={mode !== 'view'}
                  showSubmit={mode !== 'view'}
                  onSubmitClick={onSubmitClick}
                  submitLoading={isSubmitting}
                  onCancelClick={(e) => {
                    resetForm();
                    onCancelClick?.(e);
                  }}
                />
              </>
            )}
          </>
        </>
      )}
    </MtFormContainer>
  );
};
